import React, { createContext, useContext, useReducer } from 'react';
import dragAndDropReducer from './FreeformAnalysisFormUtil/dragAndDropReducer';
import sectionsReducer from './FreeformAnalysisFormUtil/sectionsReducer';

const initSections = (value) => {
  const currentSections = (value && value.initialSections) || [];
  const nonLaidOutLibraryItems = (value && value.nonLaidOutLibraryItems) || [];

  return {
    currentSections,
    nonLaidOutLibraryItems,
  };
};

const initDragAndDropState = () => {
  return {
    draggableBetweenSectionsItemId: null,
    currentDroppableSectionId: null,
    currentPosition: null,
    draggableWithinSectionsItemId: null,
  };
};

const SectionsStateContext = createContext(null);
const SectionsDispatchContext = createContext(null);

function useSectionsState() {
  const context = useContext(SectionsStateContext);
  if (context === undefined) {
    throw new Error('useSectionsState must be used within a SectionsProvider');
  }
  return context;
}

function useSectionsDispatch() {
  const context = useContext(SectionsDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useSectionsDispatch must be used within a SectionsProvider'
    );
  }
  return context;
}
function useSections() {
  return [useSectionsState(), useSectionsDispatch()];
}

const combineDispatch =
  (...dispatches) =>
  (action) =>
    dispatches.forEach((dispatch) => dispatch(action));

const SectionsProvider = ({ value, children }) => {
  const [sectionsState, sectionsDispatch] = useReducer(
    sectionsReducer,
    value,
    initSections
  );

  const [dragAndDropState, dragAndDropDispatch] = useReducer(
    dragAndDropReducer,
    value,
    initDragAndDropState
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const combinedDispatch = React.useCallback(
    combineDispatch(sectionsDispatch, dragAndDropDispatch),
    [sectionsDispatch, dragAndDropDispatch]
  );
  const combinedState = React.useMemo(
    () => ({ ...dragAndDropState, ...sectionsState }),
    [dragAndDropState, sectionsState]
  );

  return (
    <SectionsStateContext.Provider value={combinedState}>
      <SectionsDispatchContext.Provider value={combinedDispatch}>
        {children}
      </SectionsDispatchContext.Provider>
    </SectionsStateContext.Provider>
  );
};

const DragAndDropContext = React.createContext({});
const DragAndDropContextProvider = ({ value, children }) => (
  <DragAndDropContext.Provider value={value}>
    {children}
  </DragAndDropContext.Provider>
);
function useDragAndDrop() {
  const context = useContext(DragAndDropContext);
  if (context === undefined) {
    throw new Error(
      'useDragAndDrop must be used within a DragAndDropContextProvider'
    );
  }
  return context;
}

const RTEContext = React.createContext({});
const RTEContextProvider = ({ value, children }) => (
  <RTEContext.Provider value={value}>{children}</RTEContext.Provider>
);
function useRTE() {
  const context = useContext(RTEContext);
  if (context === undefined) {
    throw new Error('useRTE must be used within a RTEContextProvider');
  }
  return context;
}

export {
  SectionsProvider,
  DragAndDropContextProvider,
  useSectionsState,
  useSectionsDispatch,
  useSections,
  useDragAndDrop,
  RTEContextProvider,
  useRTE,
};
